import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import { ui } from "../../../shared/styles/uiSystem"

function CollapseButton({ collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...ui.secondaryButton,
        minWidth: 42,
        minHeight: 36
      }}
    >
      {collapsed ? "+" : "−"}
    </button>
  )
}

function MetricCard({ label, value, helper }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "grid",
        gap: 4,
        alignContent: "start"
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 20,
          lineHeight: 1.05,
          fontWeight: 900,
          color: "#0f172a"
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            lineHeight: 1.35
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  )
}

function ProjectionHero({ monthLabel, projectedRevenue, scheduledRevenue, deltaLabel }) {
  return (
    <div
      style={{
        ...ui.infoCard,
        padding: 14,
        display: "grid",
        gap: 8
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div style={{ display: "grid", gap: 4 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 800,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: 0.3
            }}
          >
            Mes actual
          </div>

          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: "#0f172a"
            }}
          >
            {monthLabel}
          </div>
        </div>

        <div
          style={{
            padding: "6px 10px",
            borderRadius: 999,
            background: "#ffffff",
            border: "1px solid #dbeafe",
            color: "#1d4ed8",
            fontSize: 12,
            fontWeight: 900
          }}
        >
          Vista compacta
        </div>
      </div>

      <div style={{ display: "grid", gap: 4 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: "#475569"
          }}
        >
          Proyección mensual
        </div>

        <div
          style={{
            fontSize: 30,
            lineHeight: 1,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          ${projectedRevenue}
        </div>

        <div
          style={{
            fontSize: 12.5,
            color: "#475569",
            lineHeight: 1.4
          }}
        >
          Programado actual: ${scheduledRevenue} · {deltaLabel}
        </div>
      </div>
    </div>
  )
}

export default function MonthlyForecastPanel({
  monthlyForecast,
  collapsed,
  onToggle,
  formatMoney
}) {
  const monthLabel = normalizeMonth(monthlyForecast?.month)
  const totalAppointments = toNumber(monthlyForecast?.totalAppointments)
  const avgTicket = toNumber(monthlyForecast?.avgTicketPerAppointment)
  const scheduledRevenue = toNumber(monthlyForecast?.scheduledRevenue)
  const projectedRevenue = toNumber(monthlyForecast?.projectedRevenue)
  const delta = projectedRevenue - scheduledRevenue

  const formattedAppointments = formatCount(totalAppointments)
  const formattedAvgTicket = formatMoney(avgTicket)
  const formattedScheduledRevenue = formatMoney(scheduledRevenue)
  const formattedProjectedRevenue = formatMoney(projectedRevenue)
  const formattedDelta = formatMoney(Math.abs(delta))

  const deltaLabel =
    delta > 0
      ? `proyección superior por $${formattedDelta}`
      : delta < 0
        ? `proyección por debajo en $${formattedDelta}`
        : "sin diferencia contra lo programado"

  return (
    <SectionCard
      title="Pronóstico mensual"
      subtitle="Resumen compacto del mes"
      actions={
        <CollapseButton
          collapsed={collapsed}
          onClick={onToggle}
        />
      }
    >
      {!collapsed && (
        <div style={{ display: "grid", gap: 10 }}>
          <ProjectionHero
            monthLabel={monthLabel}
            projectedRevenue={formattedProjectedRevenue}
            scheduledRevenue={formattedScheduledRevenue}
            deltaLabel={deltaLabel}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 10,
              alignItems: "start"
            }}
          >
            <MetricCard
              label="Citas programadas"
              value={formattedAppointments}
              helper="Citas registradas para este mes"
            />

            <MetricCard
              label="Ticket promedio"
              value={`$${formattedAvgTicket}`}
              helper="Ingreso estimado por cita"
            />

            <MetricCard
              label="Ingreso programado"
              value={`$${formattedScheduledRevenue}`}
              helper="Suma actual agendada"
            />

            <MetricCard
              label="Diferencia"
              value={delta === 0 ? "$0" : `${delta > 0 ? "+" : "-"}$${formattedDelta}`}
              helper={
                delta > 0
                  ? "La proyección supera lo ya programado"
                  : delta < 0
                    ? "Lo programado va por encima de la proyección"
                    : "Sin variación relevante"
              }
            />
          </div>
        </div>
      )}
    </SectionCard>
  )
}

function toNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatCount(value) {
  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(toNumber(value))
}

function normalizeMonth(value) {
  if (!value) return "-"

  const raw = String(value).trim()

  if (/^\d{4}-\d{2}$/.test(raw)) {
    const [year, month] = raw.split("-")
    const date = new Date(Number(year), Number(month) - 1, 1)

    return new Intl.DateTimeFormat("es-MX", {
      month: "long",
      year: "numeric"
    }).format(date)
  }

  return raw
}